import { importLibrary } from '@googlemaps/js-api-loader';
import { useEffect, useRef, useState } from 'react';
import type { ScheduleRoutes, ScheduleRoutesJobAddress } from './models/JobScheduleInterfaces';

interface GoogleMapsProps {
    routes: ScheduleRoutes[];
}

const GoogleMap = (props: GoogleMapsProps) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const[mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

    // Track active polylines so we can clear them when recalculating routes
    const activePolylinesRef = useRef<google.maps.Polyline[]>([]);
    const activeMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        const { Map } = await importLibrary("maps");
        const office = new google.maps.LatLng(-37.817253, 145.299153);
        
        if (!mapRef.current) return;

        const map = new Map(mapRef.current, {
            center: office,
            zoom: 8,
            mapId: "allocationMap"
        });
        setMapInstance(map);

        // Initialise a single shared native InfoWindow instance
        infoWindowRef.current = new google.maps.InfoWindow({
            disableAutoPan: false // Smoothly pans the map view to fit the open popup box
        });

    }

    useEffect(() => {
        if (!mapInstance) return;
        addRoutes();
    }, [mapInstance]);

    const addRoutes = async () => {
        const { Route } = await importLibrary("routes");
        const { AdvancedMarkerElement } = await importLibrary("marker");
        const bounds = new google.maps.LatLngBounds();

        //clear lines and markers
        activePolylinesRef.current.forEach(polyline => polyline.setMap(null));
        activePolylinesRef.current = [];
        activeMarkersRef.current.forEach(m => m.map = null);
        activeMarkersRef.current = [];
        if (infoWindowRef && infoWindowRef.current) {
            infoWindowRef.current.close();
        }

        const office = new google.maps.LatLng(-37.817253, 145.299153);
        //get a list of route colours to use to separate the routes
        var routeColours = ["blue", "red", "green", "orange", "purple", "pink", "yellow"];

        var latlnglist: google.maps.LatLng[] = [];
        latlnglist.push(office);

        for (var i = 0; i < props.routes.length; i++) {
            //get way points
            var waypts: google.maps.routes.Waypoint[] = [];
            for (var a = 0; a < props.routes[i].jobAddresses.length; a++) {
                var waypt = new google.maps.LatLng
                    ({
                        lat: props.routes[i].jobAddresses[a].lat,
                        lng: props.routes[i].jobAddresses[a].lng
                    });
                latlnglist.push(waypt);
                waypts.push({ location: waypt, vehicleStopover: true });
                //add waypt marker popup
                if (mapInstance) {
                    var wayPtMarker = createMarker(mapInstance, props.routes[i].jobAddresses[a], props.routes[i].truckName)
                    activeMarkersRef.current.push(wayPtMarker);
                }
            }
            latlnglist.push(office);

            //add the google route with way points
            var googleRoute: google.maps.routes.ComputeRoutesRequest = {
                origin: office,
                destination: office,
                intermediates: waypts,
                travelMode: 'DRIVING',
                fields: ["path", "legs"]
            };

            const { routes } = await Route.computeRoutes(googleRoute);

            if (routes) {
                const mainRoute = routes[0];

                // Build native vectors directly onto the DOM instance canvas
                const newLines = mainRoute.createPolylines({
                    polylineOptions: {
                        map: mapInstance,
                        strokeColor: routeColours[i % routeColours.length],
                        strokeWeight: 5,
                        strokeOpacity: 0.9,
                    },
                });

                //Add lines
                newLines.forEach((polyline) => {
                    // Overlay the polyline onto our active map object
                    polyline.setMap(mapInstance);
                    activePolylinesRef.current.push(polyline);

                    // Extend map viewport boundaries to contain the complete path
                    polyline.getPath().forEach((latLng) => bounds.extend(latLng));
                });

                //Add Markers
                // This places markers labeled 'A', 'B', etc. at your intermediate stops
                //const waypointMarkers = await mainRoute.createWaypointAdvancedMarkers({
                //    map: mapInstance
                //});
                //activeMarkersRef.current.push(...waypointMarkers);

                const mainRoutePath = mainRoute.path;
                if (mainRoutePath && mainRoutePath.length > 0) {
                    const startCoords = mainRoutePath[0];

                    //add a start / end marker as both are the office
                    const startEndElement = document.createElement('div');
                    startEndElement.innerHTML = `
                        <div style="background-color: #34A853; color: white; padding: 6px 10px; border-radius: 20px; font-weight: bold; font-family: sans-serif; box-shadow: 0 2px 6px rgba(0,0,0,0.3); border: 2px solid white;">
                          Bayswater Office
                        </div>
                    `;

                    // Instantiate Advanced Markers
                    const startMarker = new AdvancedMarkerElement({
                        map: mapInstance,
                        position: startCoords,
                        content: startEndElement,
                        title: "Bayswater Office"
                    });

                    activeMarkersRef.current.push(startMarker);
                }
            }

            // Refit the camera view cleanly around all constructed route layouts
            if (mapInstance) {
                mapInstance.fitBounds(bounds);
            }
        }
    }
    
    //Add a summary panel at some point later
    //                const summaryPanel = document.getElementById(
    //                    "directions-panel"
    //                ) as HTMLElement;
    //                if (summaryPanel) {
    //                    summaryPanel.innerHTML += "";

    //                    // For each route, display summary information.
    //                    for (let i = 0; i < route.legs.length; i++) {
    //                        const routeSegment = i + 1;
    //                        summaryPanel.innerHTML +=
    //                            "<b>Route Segment: " + routeSegment + "</b><br>";
    //                        summaryPanel.innerHTML += route.legs[i].start_address + " to ";
    //                        summaryPanel.innerHTML += route.legs[i].end_address + "<br>";
    //                        summaryPanel.innerHTML += route.legs[i].distance!.text + "<br><br>";
    //                    }
    //                }


    const createMarker = (mapInstance: google.maps.Map, wayPoint: ScheduleRoutesJobAddress, truckName: string) => {
        
        var position = { lat: wayPoint.lat, lng: wayPoint.lng };
        var address = wayPoint.address;

        var markerText = getMarkerText(address, truckName);

        var infopopup = new google.maps.InfoWindow({ content: markerText });
        const marker = new google.maps.marker.AdvancedMarkerElement({
            position: position,
            map: mapInstance,
            title: truckName
        });

        google.maps.event.addListener(marker, 'click', function () {
            //infowindow.setContent(markerText);
            infopopup.open(mapInstance, marker);
        });

        google.maps.event.addListener(mapInstance, 'click', (function (infopopup) {
            return function () {
                infopopup.close();
            }
        })(infopopup));

        return marker;
    }

    const getMarkerText = (address: string, name: string) => {
        return '<div>' +
            '<h3>' + address + '</h3>' +
            '<span>' + name + '</span>' +
            '</div>';
    }

    return (
        <div>
            <div ref={mapRef} className="map" id="map"></div>
            {/*<div id="directions-panel"></div>*/}
        </div>
    );
    
}

export default GoogleMap;